export interface DatadogConfig {
	applicationId: string;
	clientToken: string;
	site: string;
	service: string;
	env: string;
	version: string;
	sessionSampleRate: number;
	sessionReplaySampleRate: number;
	defaultPrivacyLevel: string;
	trackUserInteractions: boolean;
	trackResources: boolean;
	trackLongTasks: boolean;
	enableExperimentalFeatures: string[];
}

export interface CustomMetricData {
	name?: string;
	value?: number;
	tags?: string[];
	/** ISO 8601. Every action already sends the string form. */
	timestamp?: string;
	// Portal-specific properties
	property_id?: string;
	search_query?: string;
	results_count?: number;
	filters?: any;
	lead_type?: string;
	form_data?: any;
	feature_name?: string;
	category?: string;
	web_vital?: string;
	rating?: string;
	// Generic properties for extensibility
	[key: string]: any;
}

export interface WebVitalReport {
	readonly name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';
	readonly value: number;
	readonly rating: 'good' | 'needs-improvement' | 'poor';
	/** Epoch ms at which the measured interval began. */
	readonly startedAt: number;
	/** Page the metric belongs to, captured when web-vitals reported it. */
	readonly url: string;
	/** Pathname of `url`. Kept apart because the RUM dashboards filter on it. */
	readonly path: string;
}

// Global window interface extensions for Datadog RUM and Logs
declare global {
	interface Window {
		DD_RUM?: {
			init: (config: any) => void;
			addAction: (name: string, context?: any) => void;
			addError: (error: Error, context?: any) => void;
			addTiming: (name: string, value: number) => void;
			setUser: (user: any) => void;
			clearUser: () => void;
			addGlobalContextProperty: (context: any) => void;
			removeGlobalContextProperty: (key: string) => void;
			setGlobalContext: (context: any) => void;
			clearGlobalContext: () => void;
			getGlobalContext: () => any;
			startView: (name: string, context?: any) => void;
			startSessionReplayRecording: () => void;
			stopSessionReplayRecording: () => void;
			onReady: (callback: () => void) => void;
		};
		DD_LOGS?: {
			logger: {
				debug: (message: string, context?: any) => void;
				info: (message: string, context?: any) => void;
				warn: (message: string, context?: any) => void;
				error: (message: string, context?: any) => void;
			};
		};
	}
}

export enum MetricActionName {
	PropertyView = 'property_view',
	PropertySearch = 'property_search',
	PropertyFilter = 'property_filter',
	PropertyContact = 'property_contact',
	PropertyFavorite = 'property_favorite',
	PropertyShare = 'property_share',
	UserRegistration = 'user_registration',
	UserLogin = 'user_login',
	FormSubmission = 'form_submission',
	MapInteraction = 'map_interaction',
	PhotoVisualization = 'photo_visualization',
	VirtualTour = 'virtual_tour',
	CalculatorUsage = 'calculator_usage',
	RegionExploration = 'region_exploration'
}

export enum ErrorCategory {
	API = 'api_error',
	Authentication = 'auth_error',
	UI = 'ui_error',
	Performance = 'performance_error',
	Network = 'network_error',
	Form = 'form_error',
	Payment = 'payment_error',
	Integration = 'integration_error'
}

export interface DatadogError {
	message: string;
	source: 'network' | 'source' | 'console' | 'logger' | 'custom';
	category?: ErrorCategory;
	stack?: string;
	fingerprint?: string;
	startTime?: number;
}
